import React, { useState, useRef, useEffect } from 'react'
import { withPlugin } from '@flamingo_tech/funkgo'

import Image from '../../common/Image/Image'
import Pressable from '../../common/Pressable/Pressable'

import styles from './ProductSizeGuide.module.css'
import cx from '../../../utils/className'

/**
 * 尺码表三态布局：
 * 1）single：仅 1 列；
 * 2）split（H5 总列数<=4，PC 总列数<=5）：首列按内容，剩余列等分；
 * 3）intrinsic（超过阈值）：所有列按内容宽度，容器横向滚动。
 */
const ProductSizeGuideTable = props => {
  const {
    rows,
    isDesktop,
    activeTrSub,
    activeTdSub,
    onActivate,
    onDeactivate,
  } = props

  const totalColCount = rows[0]?.length || 0
  const restColCount = totalColCount > 0 ? totalColCount - 1 : 0
  const splitMaxTotalCol = isDesktop ? 5 : 4
  const useSplitLayout = totalColCount > 1 && totalColCount <= splitMaxTotalCol

  const layoutModeClass =
    totalColCount <= 1
      ? styles.sizeGuideLayoutSingle
      : useSplitLayout
        ? styles.sizeGuideLayoutSplit
        : styles.sizeGuideLayoutIntrinsicRest

  const wrapProps =
    totalColCount > 0
      ? { style: { '--sg-rest': restColCount, '--sg-cols': totalColCount } }
      : undefined

  const renderTd = (td, trIndex, tdIndex) => {
    const activeTr = activeTrSub === trIndex
    const activeTd = activeTdSub === tdIndex
    const interactive = trIndex !== 0 && tdIndex !== 0
    const cellClass = cx(
      styles.tdElement,
      styles.sizeGuideTd,
      activeTdSub === tdIndex && styles.active,
      activeTr && activeTd && styles.highlight,
    )

    if (isDesktop) {
      return (
        <td
          key={tdIndex}
          onMouseOver={interactive ? () => onActivate(trIndex, tdIndex) : undefined}
          onMouseLeave={interactive ? onDeactivate : undefined}
          className={cellClass}
        >
          <div><span>{td}</span></div>
        </td>
      )
    }

    return (
      <td
        key={tdIndex}
        onClick={interactive ? () => onActivate(trIndex, tdIndex) : undefined}
        className={cellClass}
      >
        <div><span>{td}</span></div>
      </td>
    )
  }

  return (
    <div
      className={cx(styles.sizeGuideTableWrap, layoutModeClass)}
      {...wrapProps}
    >
      <table className={cx(styles.table, styles.sizeGuideGridTable)}>
        <tbody>
          {rows.map((tr, trIndex) => {
            const activeTr = activeTrSub === trIndex
            return (
              <tr key={trIndex} className={cx(styles.trElement, activeTr && styles.active)}>
                {tr.map((td, tdIndex) => renderTd(td, trIndex, tdIndex))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const SwitchBtn = props => {
  return (
    <Pressable
      className={cx(styles.switchBtn, props.currentIndex === 0 ? styles.left : styles.right)}
      onClick={props.onClick}
      ariaLabel={props.$i18n.transl('core.product.toggleUnits')}
    >
      <div className={styles.selected} aria-hidden="true">{props.currentIndex === 0 ? 'in.' : 'cm'}</div>
      <div className={styles.bg} aria-hidden="true">
        <span>in.</span>
        <span>cm</span>
      </div>
    </Pressable>
  )
}

const ProductSizeGuide = props => {
  const { countryCode } = props.$site.getSiteInfo()
  const isDesktop = props.$detector.isDesktop()
  const [bodySwitchBtnCurrentIndex, setBodySwitchBtnCurrentIndex] = useState(countryCode === 'US' ? 0 : 1)
  const [productSwitchBtnCurrentIndex, setProductSwitchBtnCurrentIndex] = useState(countryCode === 'US' ? 0 : 1)
  const [fitTabIndex, setFitTabIndex] = useState(0)
  const [inseamTabIndex, setInseamTabIndex] = useState(0)

  // SIZE
  const [trSub, setTrSub] = useState(-1)
  const [tdSub, setTdSub] = useState(-1)

  // INSEAM
  const [inseamTrSub, setInseamTrSub] = useState(-1)
  const [inseamTdSub, setInseamTdSub] = useState(-1)

  // Anchor tab active index
  const [activeSectionIndex, setActiveSectionIndex] = useState(0)

  // Stable ref that always holds the latest section refs array,
  // so the scroll handler never reads a stale closure value
  const sectionRefsForScroll = useRef([])

  // Section refs for scroll navigation
  const anchorTabsRef = useRef(null)
  const bodySectionRef = useRef(null)
  const productSectionRef = useRef(null)
  const howToMeasureRef = useRef(null)
  const hasBodySectionRef = useRef(false)
  const lackBodyDataReportedRef = useRef(false)
  const trackRef = useRef(props.$track)
  trackRef.current = props.$track

  // Find the nearest scrollable ancestor (handles modal / drawer containers)
  const getScrollParent = node => {
    if (!node || node === document.body) return window
    const { overflowY } = window.getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') return node
    return getScrollParent(node.parentElement)
  }

  const handleOnClick = () => {
    setBodySwitchBtnCurrentIndex(bodySwitchBtnCurrentIndex === 0 ? 1 : 0)
  }

  const handleProductOnClick = () => {
    setProductSwitchBtnCurrentIndex(productSwitchBtnCurrentIndex === 0 ? 1 : 0)
  }

  const transl = key => {
    return props.$i18n.transl(`core.product.sizeGuideInfo.${key}`)
  }

  const handleItemClick = (trIndex, tdIndex) => {
    setTrSub(trIndex)
    setTdSub(tdIndex)
  }

  const handleInseamItemClick = (trIndex, tdIndex) => {
    setInseamTrSub(trIndex)
    setInseamTdSub(tdIndex)
  }

  const sizeGuideInfo = [
    {
      title: 'yourBust',
      content: 'yourBustContent'
    },
    {
      title: 'yourWaist',
      content: 'yourWaistContent'
    },
    {
      title: 'yourHips',
      content: 'yourHipsContent'
    }
  ]

  const sizeList = ((props.sizeGuideTab[fitTabIndex] || {}).sizeChartTemplate || {})[bodySwitchBtnCurrentIndex === 0 ? 'sizeInchInfo' : 'sizeCmInfo'] || []
  const inseamList = ((props.sizeGuideTab[inseamTabIndex] || {}).inseamChartTemplate || {})[productSwitchBtnCurrentIndex === 0 ? 'inchInfo' : 'cmInfo'] || []
  let showInseamMeasurementGuide = false
  props.sizeGuideTab.forEach(tab => {
    if (tab.inseamChartTemplate && tab.inseamChartTemplate.inchInfo && (tab || {}).inseamChartTemplate.inchInfo.length > 0) {
      showInseamMeasurementGuide = true
      return
    }
  })

  const hasBodySection = props.sizeGuideTab.some(tab =>
    (tab.sizeChartTemplate?.sizeInchInfo?.length > 0) || (tab.sizeChartTemplate?.sizeCmInfo?.length > 0)
  )
  const hasProductSection = props.sizeGuideTab.some(tab =>
    (tab.inseamChartTemplate?.inchInfo?.length > 0) || (tab.inseamChartTemplate?.cmInfo?.length > 0)
  )
  const hasHowToMeasureSection = !!(props.bodyMeasurementDisplayFlag || showInseamMeasurementGuide)

  hasBodySectionRef.current = hasBodySection

  // Body 尺码表无数据：短延迟后再上报，避免 sizeGuideTab 异步补全时误报；同一实例只打一次。
  // 依赖只放 hasBodySection；$track 用 ref 持有，避免父组件重渲染时 $track 引用变化反复清空定时器。
  useEffect(() => {
    if (hasBodySectionRef.current) return
    if (lackBodyDataReportedRef.current) return

    const timerId = window.setTimeout(() => {
      if (hasBodySectionRef.current) return
      if (lackBodyDataReportedRef.current) return
      lackBodyDataReportedRef.current = true
      const track = trackRef.current
      if (track && typeof track.event === 'function') {
        track.event('ProductSizeChart', 'lack_data')
      }
    }, 300)

    return () => window.clearTimeout(timerId)
  }, [hasBodySection])

  // Build the ordered list of anchor tabs (only include sections that exist)
  const sectionDefs = [
    { labelKey: 'bodyMeasurements', ref: bodySectionRef, show: hasBodySection },
    { labelKey: 'productMeasurements', ref: productSectionRef, show: hasProductSection },
    { labelKey: 'howToMeasure', ref: howToMeasureRef, show: hasHowToMeasureSection },
  ].filter(s => s.show)

  const showAnchorTabs = sectionDefs.length > 1

  // Keep sectionRefsForScroll in sync on every render (refs are stable objects)
  sectionRefsForScroll.current = sectionDefs.map(d => d.ref)

  // While a tab click is animating, suppress the scroll handler so it
  // doesn't clobber the index we just set (smooth scroll fires events
  // before the DOM has actually moved)
  const isManualScrolling = useRef(false)
  const manualScrollTimer = useRef(null)

  // Click tab: scroll to section, offset by sticky tab bar height.
  // Keep this gap in sync with handleScroll threshold to avoid active-tab flicker.
  const ACTIVE_GAP_PX = 8
  const handleSectionTabClick = (ref, index) => {
    setActiveSectionIndex(index)
    if (!ref.current || !anchorTabsRef.current) return

    // Lock out the scroll handler for the duration of the smooth scroll
    isManualScrolling.current = true
    clearTimeout(manualScrollTimer.current)
    manualScrollTimer.current = setTimeout(() => {
      isManualScrolling.current = false
    }, 800)

    const scrollParent = getScrollParent(ref.current.parentElement)
    const threshold = anchorTabsRef.current.getBoundingClientRect().bottom + ACTIVE_GAP_PX

    if (scrollParent === window) {
      const delta = ref.current.getBoundingClientRect().top - threshold
      window.scrollTo({ top: window.scrollY + delta, behavior: 'smooth' })
    } else {
      const delta = ref.current.getBoundingClientRect().top - threshold
      scrollParent.scrollBy({ top: delta, behavior: 'smooth' })
    }
  }

  // Scroll listener: the active section is the last one whose top edge has
  // passed the sticky tab bar. Always deterministic, no observer race conditions.
  useEffect(() => {
    if (!showAnchorTabs) return

    const scrollParent = anchorTabsRef.current
      ? getScrollParent(anchorTabsRef.current.parentElement)
      : window

    const target = scrollParent === window ? window : scrollParent

    const handleScroll = () => {
      // Ignore scroll events that are part of a programmatic tab-click animation
      if (isManualScrolling.current) return

      // Use the tab bar's actual bottom position in viewport coordinates.
      // This works regardless of whether we're in a modal, drawer, or full page —
      // no need to separately compute container offset + tab height.
      const threshold = anchorTabsRef.current
        ? anchorTabsRef.current.getBoundingClientRect().bottom + ACTIVE_GAP_PX
        : ACTIVE_GAP_PX

      let nextActive = 0
      sectionRefsForScroll.current.forEach((ref, index) => {
        if (!ref.current) return
        const top = ref.current.getBoundingClientRect().top
        if (top <= threshold) {
          nextActive = index
        }
      })
      setActiveSectionIndex(nextActive)
    }

    target.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => target.removeEventListener('scroll', handleScroll)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBodySection, hasProductSection, hasHowToMeasureSection])

  return (
    <div className={cx(styles.wrapper, isDesktop && styles.isDesktop, props.className)}>

      {/* ── Anchor Tabs ── */}
      {showAnchorTabs && (
        <div ref={anchorTabsRef} className={styles.anchorTabs}>
          {sectionDefs.map((def, index) => (
            <div
              key={def.labelKey}
              className={cx(styles.anchorTab, index === activeSectionIndex && styles.active)}
              onClick={() => handleSectionTabClick(def.ref, index)}
            >
              {transl(def.labelKey)}
            </div>
          ))}
        </div>
      )}

      <div className={styles.contentContainer}>
        <div className={styles.currentFitContent}>
          {
            hasBodySection &&
            <div ref={bodySectionRef} className={styles.section}>
              <div className={styles.tableBox}>
                <div className={styles.header}>{transl('bodyMeasurements')}</div>
                <div className={styles.fitTab}>
                  {
                    props.sizeGuideTab && props.sizeGuideTab.length > 1 &&
                    props.sizeGuideTab.map((tab, index) => (
                      <Pressable key={index} className={cx(styles.fitTabItem, index === fitTabIndex ? styles.active : '')} onClick={() => setFitTabIndex(index)} ariaLabel={`${props.$i18n.transl('core.product.toggleFitTab')}: ${tab.fitDisplayName}`}>{tab.fitDisplayName}</Pressable>
                    ))
                  }
                </div>
                <ProductSizeGuideTable
                  rows={sizeList}
                  isDesktop={isDesktop}
                  activeTrSub={trSub}
                  activeTdSub={tdSub}
                  onActivate={handleItemClick}
                  onDeactivate={() => handleItemClick(-1, -1)}
                />
              </div>
              <div className={styles.switchBtnWrapper}><SwitchBtn currentIndex={bodySwitchBtnCurrentIndex} onClick={handleOnClick} $i18n={props.$i18n} /></div>
            </div>
          }
          {
            hasProductSection &&
            <div ref={productSectionRef} className={styles.section}>
              <div className={styles.tableBox}>
                <div className={styles.header}>{transl('productMeasurements')}</div>
                <div className={styles.fitTab}>
                  {
                    props.sizeGuideTab && props.sizeGuideTab.length > 1 &&
                    props.sizeGuideTab.map((tab, index) => (
                      <Pressable key={index} className={cx(styles.fitTabItem, index === inseamTabIndex ? styles.active : '')} onClick={() => setInseamTabIndex(index)} ariaLabel={`${props.$i18n.transl('core.product.toggleInseamTab')}: ${tab.fitDisplayName}`}>{tab.fitDisplayName}</Pressable>
                    ))
                  }
                </div>
                <ProductSizeGuideTable
                  rows={inseamList}
                  isDesktop={isDesktop}
                  activeTrSub={inseamTrSub}
                  activeTdSub={inseamTdSub}
                  onActivate={handleInseamItemClick}
                  onDeactivate={() => handleInseamItemClick(-1, -1)}
                />
              </div>
              <div className={styles.switchBtnWrapper}><SwitchBtn currentIndex={productSwitchBtnCurrentIndex} onClick={handleProductOnClick} $i18n={props.$i18n} /></div>
            </div>
          }
          {
            (hasBodySection || hasProductSection) &&
            <div className={styles.guideTips}>{transl('sizeOffset')}</div>
          }
        </div>
        {
          (props.bodyMeasurementDisplayFlag || showInseamMeasurementGuide) &&
          <div ref={howToMeasureRef} className={styles.howToMeasureSection}>
            {
              props.bodyMeasurementDisplayFlag &&
              <div className={styles.guideInfo}>
                <div className={styles.leftText}>
                  <h3 className={styles.infoHeader}>{transl('howToMeasure')}</h3>
                  {
                    sizeGuideInfo.map((item, index) => (
                      <div key={index}>
                        <h3>{index + 1}. {transl(item.title)}</h3>
                        <p>{transl(item.content)}</p>
                      </div>
                    ))
                  }
                </div>
                <Image className={styles.guideImage} src="https://img.flamingo.shop/p/show/2dc20e588ec7437d8396205b3bee81b1.png" original/>
              </div>
            }
            {
              showInseamMeasurementGuide &&
              <div className={styles.guideInfo}>
                <div className={styles.leftText}>
                  <h3>{transl('inseamLength')}</h3>
                  <p>{transl('inseamLengthContent')}</p>
                </div>
                <Image className={cx(styles.guideImage, styles.inseamImage)} src="https://img.flamingo.shop/p/show/0f8ad370b4b444e38805bdf5f13f22b8.png" original/>
              </div>
            }
          </div>
        }
      </div>
    </div>
  )
}

export default withPlugin(ProductSizeGuide)